import api from './auth.api';

// Types
export interface Invoice {
  id: number;
  invoice_number: string;
  invoice_date: string | null;
  due_date: string | null;
  amount_total: number;
  amount_paid: number;
  amount_outstanding: number;
  currency: string;
  payment_status: 'unpaid' | 'partially_paid' | 'paid' | 'cancelled' | 'unknown';
  due_status: 'paid' | 'overdue' | 'due_today' | 'upcoming' | 'unknown';
  due_status_description: string;
  reminder_paused: boolean;
  last_synced_at: string;
  paid_at: string | null;
  pdf_url?: string | null;
  has_pdf?: boolean;
  subscriber: {
    id: number;
    name: string;
    email: string;
    mobile: string;
    subscriber_code?: string;
  };
  site: {
    id: number;
    crmSiteId: number;
    name: string;
  };
}

export interface InvoiceDetail extends Invoice {
  crm_invoice_id: number;
  crm_site_id: number;
  crm_subscriber_id: number;
  crm_status_raw: string;
  cancelled_at: string | null;
  created_at: string;
  updated_at: string;
  reminders: Array<{
    id: number;
    channel: string;
    status: string;
    sent_at: string | null;
    delivered_at: string | null;
    created_at: string;
  }>;
  pdf_link: {
    id: number;
    local_proxy_url: string;
    expires_at: string;
    s3_url?: string | null;
  } | null;
  pdf_url?: string | null;
  has_pdf?: boolean;
}

export interface InvoiceStats {
  total: number;
  due_today: number;
  overdue: number;
  paid: number;
  unpaid: number;
  total_outstanding: number;
}

export interface PaginationData {
  current_page: number;
  total_pages: number;
  per_page: number;
  total_items: number;
}

export interface ListInvoicesParams {
  page?: number;
  per_page?: number;
  site_id?: number;
  subscriber_id?: number;
  due_status?: 'paid' | 'overdue' | 'due_today' | 'upcoming' | 'unknown';
  payment_status?: 'unpaid' | 'partially_paid' | 'paid' | 'cancelled' | 'unknown';
  search?: string;
}

export interface PDFTokenResponse {
  url: string;
  expires_at: string | null;
  cached: boolean;
  source?: 's3' | 'proxy';
}

export function isPublicS3PdfUrl(url: string | null | undefined): boolean {
  if (!url) return false;
  return /https?:\/\/[^/]*s3[.-][^/]+\.amazonaws\.com\//i.test(url);
}

// API functions
export const invoicesApi = {
  /**
   * List invoices with filters and pagination
   */
  listInvoices: async (params: ListInvoicesParams = {}): Promise<{
    invoices: Invoice[];
    pagination: PaginationData;
  }> => {
    const queryParams = new URLSearchParams();

    if (params.page) queryParams.set('page', String(params.page));
    if (params.per_page) queryParams.set('per_page', String(params.per_page));
    if (params.site_id) queryParams.set('site_id', String(params.site_id));
    if (params.subscriber_id) queryParams.set('subscriber_id', String(params.subscriber_id));
    if (params.due_status) queryParams.set('due_status', params.due_status);
    if (params.payment_status) queryParams.set('payment_status', params.payment_status);
    if (params.search) queryParams.set('search', params.search);

    const response = await api.get(`/invoices?${queryParams.toString()}`);
    return response.data.data;
  },

  /**
   * Get invoice details by ID
   */
  getInvoice: async (id: number): Promise<InvoiceDetail> => {
    const response = await api.get(`/invoices/${id}`);
    const { invoice, subscriber, site, reminders, pdf_link } = response.data.data;
    return {
      ...invoice,
      subscriber,
      site,
      reminders,
      pdf_link,
      pdf_url: invoice.pdf_url ?? pdf_link?.s3_url ?? null,
      has_pdf: !!(invoice.pdf_url ?? pdf_link?.s3_url),
    };
  },

  /**
   * Sync all invoices (bulk sync)
   */
  bulkSync: async (): Promise<void> => {
    await api.post('/invoices/sync');
  },

  /**
   * Sync single invoice
   */
  syncInvoice: async (id: number): Promise<{
    action: 'created' | 'updated' | 'skipped' | 'failed';
    changes: string[];
  }> => {
    const response = await api.post(`/invoices/${id}/sync`);
    return response.data.data;
  },

  /**
   * Toggle reminder pause for an invoice
   */
  togglePause: async (id: number): Promise<{ reminder_paused: boolean }> => {
    const response = await api.post(`/invoices/${id}/toggle-pause`);
    return response.data.data;
  },

  /**
   * Get invoice statistics
   */
  getStats: async (): Promise<InvoiceStats> => {
    const response = await api.get('/invoices/stats');
    return response.data.data;
  },

  /**
   * Get PDF token for an invoice
   */
  getPDFToken: async (id: number): Promise<PDFTokenResponse> => {
    const response = await api.get(`/invoices/${id}/pdf`);
    return response.data.data;
  },

  /**
   * Resolve public S3 PDF URL for an invoice (authenticated).
   */
  resolvePdfUrl: async (id: number): Promise<string | null> => {
    const response = await api.get(`/invoices/${id}/pdf`);
    const url = response.data.data?.url ?? null;
    return isPublicS3PdfUrl(url) ? url : null;
  },

  /**
   * Open PDF in new tab (prefers public S3 URL when available)
   */
  openPDF: async (id: number, pdfUrl?: string | null): Promise<void> => {
    const directUrl = isPublicS3PdfUrl(pdfUrl) ? pdfUrl : await invoicesApi.resolvePdfUrl(id);
    if (directUrl) {
      window.open(directUrl, '_blank');
      return;
    }
    const { url: proxyUrl } = await invoicesApi.getPDFToken(id);
    if (!proxyUrl) {
      throw new Error('PDF URL not available');
    }
    window.open(proxyUrl, '_blank');
  },

  /**
   * Authenticated redirect endpoint (S3 or JWT proxy fallback)
   */
  downloadPdfRedirect: (id: number): string => {
    const base = api.defaults.baseURL ?? '';
    return `${base}/invoices/${id}/pdf-download`;
  },
};

export default invoicesApi;
